import { useVideosContext } from '../../context/videos-context'
import { VideoList } from './video-list'
import * as Styled from './styled'
import { useExportSocket } from '../../hooks/use-export-socket'
import { generateVideo } from '../../context/api-helpers'
import { logError } from 'utils/logger'

const ContentInner = () => {
  const { state, dispatch } = useVideosContext()
  useExportSocket()

  const handleGenerateAll = async () => {
    try {
      // Filter out videos that are already generating or rendering
      const videosToGenerate = state.videos.filter(video => {
        const isGenerating =
          video.generationState === 'generating' ||
          video.generationState === 'preparing_media' ||
          video.generationState === 'preparing_render'

        const isRendering =
          video.renderState === 'preparing' ||
          video.renderState === 'rendering' ||
          video.renderState === 'uploading'

        return !isGenerating && !isRendering
      })

      // If no videos need generation, return early
      if (videosToGenerate.length === 0) {
        return
      }

      // First, set filtered videos to generating state
      videosToGenerate.forEach(video => {
        dispatch({
          type: 'UPDATE_VIDEO',
          payload: {
            id: video.id,
            updates: {
              generationState: 'generating',
              generationProgress: 0,
              generationError: undefined
            }
          }
        })
      })

      // Generate only filtered videos in parallel
      const generatePromises = videosToGenerate.map(async video => {
        try {
          const genAiVideoHash = await generateVideo(video, state.models)
          if (genAiVideoHash) {
            dispatch({
              type: 'UPDATE_VIDEO',
              payload: {
                id: video.id,
                updates: { genAiVideoHash }
              }
            })
          } else {
            // If no hash returned, set error state
            dispatch({
              type: 'UPDATE_VIDEO',
              payload: {
                id: video.id,
                updates: {
                  generationState: 'error',
                  generationError: 'Failed to start generation'
                }
              }
            })
          }
        } catch (error) {
          dispatch({
            type: 'UPDATE_VIDEO',
            payload: {
              id: video.id,
              updates: {
                generationState: 'error',
                generationError:
                  error instanceof Error ? error.message : 'Unknown error'
              }
            }
          })
        }
      })

      await Promise.all(generatePromises)
    } catch (error) {
      logError(error)
    }
  }

  // Check if all videos are currently processing
  const isAllProcessing = state.videos.every(video => {
    const isGenerating =
      video.generationState === 'generating' ||
      video.generationState === 'preparing_media' ||
      video.generationState === 'preparing_render'

    const isRendering =
      video.renderState === 'preparing' ||
      video.renderState === 'rendering' ||
      video.renderState === 'uploading'

    return isGenerating || isRendering
  })

  return (
    <Styled.ContentContainer>
      <Styled.ScrollableContent>
        <VideoList />
      </Styled.ScrollableContent>
      <Styled.GenerateAllSection>
        <Styled.GenerateAllButton
          onClick={handleGenerateAll}
          disabled={isAllProcessing}
        >
          🚀 Generate All Videos
        </Styled.GenerateAllButton>
      </Styled.GenerateAllSection>
    </Styled.ContentContainer>
  )
}

export const Content = () => {
  return <ContentInner />
}
